import { useMemo } from 'react'
import links from 'links'
import { useAb, useFt } from 'hooks'
import { constants } from 'helpers'

import { useUser } from 'modules/user'
import { usePrivateSale } from 'modules/products'
import { useFeaturedBrandNavigationData } from 'modules/brands'
import { useCandleSubscription, useCaseSubscription, useSubscription } from 'modules/subscription'
import { useReferralInvites } from 'modules/user'
import { ExclusiveLabel } from 'components/dataDisplay'

import TheEditBanner from 'compositions/banners/TheEditBanner/TheEditBanner'
import { navMessages as messages } from 'compositions/navigation'
import ReferralNavItem from 'compositions/Header/components/ReferralNavItem/ReferralNavItem'

import {
  BuyQueueBanner,
  CasesBanner,
  CaseSubscriptionBanner,
  CandleSubscriptionBanner,
  DynamicNavigationBanner,
  FeaturedBrandBanner,
  SaksCollaborationBanner,
} from '../components/banners'


// TODO: Remove this, multiple promotions will not be launched at the same time
const getGiftHubBackgroundColor = (isHolidayEnabled: boolean, isGiftHubValentinesDayEnabled: boolean, isHolidayMothersDayEnabled: boolean) => {
  if (isGiftHubValentinesDayEnabled || isHolidayMothersDayEnabled) {
    return 'faded-pink'
  }

  if (isHolidayEnabled) {
    return 'vinous'
  }
}

const useDesktopNav = () => {
  const isTheEditEnabled = useFt(constants.features.theEdit)
  const isTheShopEnabled = useFt(constants.features.theShop)
  const isCandleSubscriptionEnabled = useFt(constants.features.candleSubscription)
  const isFragranceFamiliesPageEnabled = useFt(constants.features.fragranceFamiliesPage)
  const isNotesLibraryPageEnabled = useFt(constants.features.noteLibPages)
  const isGiftSetsPageEnabled = useFt(constants.features.giftSetsPage)
  const isSaksCollaborationEnabled = useFt(constants.features.collaborationSaks)
  const isGiftCardFraudEnabled = useFt(constants.features.giftCardFraud)
  const isHolidayEnabled = useFt(constants.features.holiday)
  const isGiftHubValentinesDayEnabled = useFt(constants.features.giftHubValentinesDay)
  const isHolidayMothersDayEnabled = useFt(constants.features.holidayMothersDay)
  const isScentHoroscopeEnabled = useFt(constants.features.scentHoroscope)
  const isShopSamplesEnabled = useFt(constants.features.shopSamples)
  const isScentbirdPodcastEntryPointsEnabled = useFt(constants.features.scentbirdPodcastEntryPoints)
  const isTravelSizeEnabled = useFt(constants.features.travelSizeFragrances)

  const { gender, hasScentProfile } = useUser()
  const { isNewReferralProgramEnabled } = useReferralInvites()

  const featuredBrandNavigationData = useFeaturedBrandNavigationData()
  const isFeaturedBrandNavEnabled = Boolean(featuredBrandNavigationData)
  const isFeaturedBrandPerfumesBannerExists = Boolean(featuredBrandNavigationData?.banners?.perfumes)
  const isFeaturedBrandColognesBannerExists = Boolean(featuredBrandNavigationData?.banners?.colognes)
  const isFeaturedBrandDiscoverBannerExists = Boolean(featuredBrandNavigationData?.banners?.discover)

  const { privateSale } = usePrivateSale()
  const isPrivateSaleEnabled = Boolean(privateSale)

  const { subscription } = useSubscription()
  const isSubscribed = subscription?.isSubscribed
  const isBuyQueueEnabled = subscription?.stateOptions?.canBuyQueue
  const canApplyTossInOffer = subscription?.canApplyTossInOffer

  const { caseSubscription } = useCaseSubscription()
  const isCaseSubscriptionEnabled = caseSubscription?.isEnabled
  const isCaseSubscriptionSelected = caseSubscription?.isSelected

  const { candleSubscription } = useCandleSubscription()
  const isCandleSubscriptionSelected = candleSubscription?.isSelected
  const isCandleSubscriptionVisible = Boolean(isCandleSubscriptionEnabled && candleSubscription?.isEnabled)

  const { stats: { available } } = useReferralInvites()
  const isReferralItemVisible = Boolean(available)

  const isTopLevelCandleClubEntryPointVisible = isCandleSubscriptionVisible && !isCandleSubscriptionSelected

  const discoverSectionBanner = isFeaturedBrandDiscoverBannerExists ? FeaturedBrandBanner : DynamicNavigationBanner
  const perfumesSectionBanner = isFeaturedBrandPerfumesBannerExists ? FeaturedBrandBanner : DynamicNavigationBanner
  const colognesSectionBanner = isFeaturedBrandColognesBannerExists ? FeaturedBrandBanner : DynamicNavigationBanner

  const giftHubBackgroundColor = getGiftHubBackgroundColor(isHolidayEnabled, isGiftHubValentinesDayEnabled, isHolidayMothersDayEnabled)

  const isUpgradeEntryPointsEnabled = useAb(constants.abTests.upgradeEntryPoints) === 'b' && subscription?.isUpgradableFromMonthlyPlan

  return useMemo(() => {
    const data: Navigation.HeaderDesktopSection[] = [
      {
        id: 'perfumes',
        title: messages.perfumes,
        list: [
          {
            id: 'perfumes-all',
            title: messages.allPerfumes,
            to: links.subscription.perfumes,
          },
          isSaksCollaborationEnabled && {
            id: 'header-discover-saks-collaboration',
            title: messages.saksCollaboration,
            to: links.saks,
            label: messages.labels.new,
          },
          {
            id: 'perfumes-new-arrivals',
            title: messages.newArrivals,
            to: links.newArrivals.perfumes,
          },
          {
            id: 'perfumes-bestsellers',
            title: messages.bestsellers,
            to: links.bestsellers.perfumes,
          },
          {
            id: 'perfumes-pom',
            title: messages.pom,
            to: links.subscription.currentPerfumeOfTheMonth,
          },
          {
            id: 'perfumes-clean',
            title: messages.clean,
            to: links.clean.perfumes,
          },
          {
            id: 'perfumes-exclusive',
            title: messages.exclusive,
            to: `${links.subscription.perfumes}?category=exclusive`,
          },
          {
            id: 'perfumes-collections',
            title: messages.collections,
            to: links.collectionsWomen,
          },
          {
            id: 'header-discover-allBrands',
            title: messages.allBrands,
            to: links.brands,
          },
        ],
        withColumns: true,
        bannerComponent: perfumesSectionBanner,
      },
      {
        id: 'colognes',
        title: messages.colognes,
        list: [
          {
            id: 'colognes-all',
            title: messages.allColognes,
            to: links.subscription.colognes,
          },
          isSaksCollaborationEnabled && {
            id: 'header-discover-saks-collaboration',
            title: messages.saksCollaboration,
            to: links.saks,
            label: messages.labels.new,
          },
          {
            id: 'colognes-new-arrivals',
            title: messages.newArrivals,
            to: links.newArrivals.colognes,
          },
          {
            id: 'colognes-bestsellers',
            title: messages.bestsellers,
            to: links.bestsellers.colognes,
          },
          {
            id: 'colognes-com',
            title: messages.com,
            to: links.subscription.currentCologneOfTheMonth,
          },
          {
            id: 'colognes-clean',
            title: messages.clean,
            to: links.clean.colognes,
          },
          {
            id: 'colognes-exclusive',
            title: messages.exclusive,
            to: `${links.subscription.colognes}?category=exclusive`,
          },
          {
            id: 'colognes-collections',
            title: messages.collections,
            to: links.collectionsMen,
          },
          {
            id: 'header-discover-allBrands',
            title: messages.allBrands,
            to: links.brands,
          },
        ],
        withColumns: true,
        bannerComponent: colognesSectionBanner,
      },
      {
        id: 'beauty',
        title: messages.beauty,
        to: links.subscription.beauty,
      },
      isUpgradeEntryPointsEnabled && {
        id: 'header-upgrade',
        title: messages.upgrade2Scents,
        to: links.upgradeProduct,
      },
      !isUpgradeEntryPointsEnabled && isTopLevelCandleClubEntryPointVisible && {
        id: 'candleClub',
        title: messages.candleSubscription,
        to: links.candleSubscription,
      },
      { isDivider: true },
      {
        id: 'shop',
        title: messages.shop,
        list: [
          isTheEditEnabled && {
            id: 'header-shop-edit',
            title: messages.edit,
            to: links.shop.theEdit,
          },
          {
            id: 'header-shop-scentbirdCases',
            title: messages.scentbirdCases,
            to: links.shop.scentbirdCases,
          },
          isTheShopEnabled && {
            id: 'header-shop-fullSizeFragrances',
            title: messages.fullSizeFragrances,
            to: gender === 'FEMALE' ? links.shop.perfumes : links.shop.colognes,
          },
          isTravelSizeEnabled && {
            id: 'header-shop-travelSizeFragrances',
            title: messages.travelSizeFragrances,
            to: gender === 'FEMALE' ? links.shop.travelSize.perfumes : links.shop.travelSize.colognes,
            label: messages.labels.new,
          },
          {
            id: 'header-shop-giftsets',
            title: messages.giftSetsPage,
            to: gender === 'MALE' ? links.giftSets.colognes : links.giftSets.perfumes,
          },
          isTheShopEnabled && {
            id: 'header-shop-candlesHomeScents',
            title: messages.candlesHomeScents,
            to: links.shop.candlesHomeScents,
          },
          isTheShopEnabled && {
            id: 'header-shop-bathBody',
            title: messages.bathBody,
            to: links.shop.bathBody,
          },
          {
            id: 'header-shop-accessories',
            title: messages.accessories,
            to: links.shop.accessories,
          },
          isShopSamplesEnabled && {
            id: 'header-shop-samples',
            title: messages.samples,
            to: links.shop.samples.root,
          },
        ].filter(Boolean),
        subNavLinksPosition: 'right',
        withColumns: true,
        bannerComponent: isTheEditEnabled ? TheEditBanner : CasesBanner,
      },
      {
        id: 'discover',
        title: messages.discover,
        list: [
          {
            id: 'header-discover-scentProfile',
            title: messages.scentProfile,
            to: hasScentProfile ? links.scentProfile : links.smartRecommendations,
          },
          isFeaturedBrandNavEnabled && {
            id: 'header-discover-featuredBrand',
            title: messages.featuredBrand,
            to: featuredBrandNavigationData.navLink,
          },
          isScentHoroscopeEnabled && {
            id: 'header-discover-scentHoroscope',
            title: messages.scentHoroscope,
            to: links.scentHoroscope,
            label: messages.labels.new,
          },
          {
            id: 'header-discover-fomArchive',
            title: messages.fomArchive,
            to: links.subscription.perfumeOfTheMonthList,
          },
          isNotesLibraryPageEnabled && {
            id: 'header-discover-notesLibrary',
            title: messages.notesLibrary,
            to: links.notes,
          },
          isFragranceFamiliesPageEnabled && {
            id: 'header-discover-fragranceFamilies',
            title: messages.fragranceFamilies,
            to: links.fragranceFamilies,
          },
          {
            id: 'header-discover-scentBlog',
            title: messages.scentBlog,
            toTab: links.blog,
          },
          isScentbirdPodcastEntryPointsEnabled && {
            id: 'header-discover-podcast',
            title: messages.scentWorldPodcast,
            href: 'https://podcast.scentbird.com/',
            label: messages.labels.new,
          },
        ].filter(Boolean),
        subNavLinksPosition: 'right',
        withColumns: true,
        // TODO: Move SAKS banner to Strapi — added on 26-01-2023 by algeas
        bannerComponent: isSaksCollaborationEnabled && !isScentbirdPodcastEntryPointsEnabled && !isHolidayMothersDayEnabled ? SaksCollaborationBanner : discoverSectionBanner,
      },
      isSubscribed && {
        id: 'member perks',
        title: messages.memberPerks,
        list: [
          isBuyQueueEnabled && {
            id: 'header-member-perks-buyYourQueue',
            title: messages.buyYourQueue,
            to: links.buyQueue,
          },
          isPrivateSaleEnabled && {
            id: 'header-member-perks-privateSale',
            title: messages.privateSale,
            to: links.privateSale,
          },
          !isNewReferralProgramEnabled && {
            id: 'header-member-perks-referralProgram',
            title: messages.referralProgram,
            to: links.invite,
          },
          isReferralItemVisible && {
            id: 'referral-nav-item',
            to: links.invite,
            title: messages.referralProgram,
            content: <ReferralNavItem spanClassName="text-white bg-gold-70 text-p4" freeFragranceClassName="text-gold-70" />,
          },
          canApplyTossInOffer && {
            id: 'header-member-perks-lastMinuteAddons',
            title: messages.lastMinuteAddons,
            to: links.tossIn,
          },
          isCaseSubscriptionEnabled && {
            id: 'header-member-perks-caseSubscription',
            title: messages.caseSubscription,
            to: links.caseSubscription,
          },
          isCandleSubscriptionVisible && {
            id: 'header-member-perks-candleSubscription',
            title: messages.candleSubscription,
            to: links.candleSubscription,
            label: isCandleSubscriptionSelected ? messages.labels.subscribed : messages.labels.new,
          },
        ].filter(Boolean),
        subNavLinksPosition: 'right',
        withColumns: false,
        bannerComponent: ({ onClick }) => {
          if (isCandleSubscriptionVisible && !isCandleSubscriptionSelected) {
            return (
              <CandleSubscriptionBanner onClick={onClick} />
            )
          }

          if (isCaseSubscriptionEnabled && !isCaseSubscriptionSelected) {
            return (
              <CaseSubscriptionBanner onClick={onClick} />
            )
          }

          return <BuyQueueBanner onClick={onClick} />
        },
      },
      {
        id: 'gifts',
        title: messages.gifts,
        list: [
          {
            id: 'header-gifts-subscription',
            title: messages.giftSubscription,
            to: links.giftSubscription,
          },
          !isGiftCardFraudEnabled && {
            id: 'header-gifts-card',
            title: messages.giftCard,
            to: links.giftCard,
          },
          isGiftSetsPageEnabled && {
            id: 'header-gift-sets',
            title: messages.giftSetsPage,
            to: links.giftSets.root,
          },
          (isHolidayEnabled || isGiftHubValentinesDayEnabled || isHolidayMothersDayEnabled) && {
            id: 'header-gift-hub',
            title: isHolidayMothersDayEnabled ? messages.holidayGiftsMothersDay : messages.holidayGifts,
            to: links.gifts,
            exclusiveLabel: <ExclusiveLabel labelText={messages.exclusives} variant={isHolidayMothersDayEnabled ? 'pink' : 'gold'} />,
          },
          !isHolidayEnabled && {
            id: 'header-gifts',
            title: messages.allGifts,
            to: links.gifts,
          },
        ].filter(Boolean),
        subNavLinksPosition: 'right',
        background: giftHubBackgroundColor,
        withColumns: false,
        bannerComponent: DynamicNavigationBanner,
      },
    ]

    return data.filter(Boolean)
  }, [
    gender,
    hasScentProfile,
    discoverSectionBanner,
    isHolidayEnabled,
    isFeaturedBrandNavEnabled,
    featuredBrandNavigationData?.navLink,
    isTheEditEnabled,
    isBuyQueueEnabled,
    canApplyTossInOffer,
    isCandleSubscriptionVisible,
    isCandleSubscriptionSelected,
    isCaseSubscriptionEnabled,
    isCaseSubscriptionSelected,
    isFragranceFamiliesPageEnabled,
    isNotesLibraryPageEnabled,
    isSubscribed,
    isTheShopEnabled,
    isPrivateSaleEnabled,
    isGiftSetsPageEnabled,
    isTopLevelCandleClubEntryPointVisible,
    isSaksCollaborationEnabled,
    isGiftCardFraudEnabled,
    isScentHoroscopeEnabled,
    isGiftHubValentinesDayEnabled,
    isHolidayMothersDayEnabled,
    isShopSamplesEnabled,
    isScentbirdPodcastEntryPointsEnabled,
    isReferralItemVisible,
    perfumesSectionBanner,
    colognesSectionBanner,
    giftHubBackgroundColor,
    isNewReferralProgramEnabled,
    isUpgradeEntryPointsEnabled,
    isTravelSizeEnabled,
  ])
}


export default useDesktopNav
